/*************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2020 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 **************************************************************************/
import {ActionButton} from '@adobe/react-spectrum';
import classNames from 'classnames';
import {IntlProvider} from '@quarry/intl';
import * as localeDataSkipMenu from './__localization__/ShellSkipMenu';
import PropTypes from 'prop-types';
import React, {useRef} from 'react';
import {useIntl} from 'react-intl';
import './ShellSkipMenu.scss';

const ShellSkipMenu = React.memo(({items = null, onVisibilityChange, open, trackSkipMenuActions}) => {
  const {formatMessage} = useIntl();
  const itemKey = items && items.length === 1 ? items[0].id : 'fallback';
  const ref = useRef(null);
  return (
    <div
      className={classNames('spectrum-Shell-skipMenu', {skipMenuOpen: open})}
      onBlur={() => onVisibilityChange(false)}
      onFocus={e => {
        onVisibilityChange(true, e);
        document.dispatchEvent(new CustomEvent('getSkipMenu', {detail: {}}));
      }}
    >
      <ActionButton
        UNSAFE_className="skipMenu-listItem"
        data-skip-menu={itemKey}
        onPress={e => {
          trackSkipMenuActions(e);
          ref.current.UNSAFE_getDOMNode().dispatchEvent(new CustomEvent('click', {bubbles: true}));
        }}
        ref={ref}
      >
        {formatMessage({
          defaultMessage: 'Skip to main content',
          description: 'Label for button to skip focus to main content',
          id: 'shellSkipMenuMainContent'
        })}
      </ActionButton>
    </div>
  );
});

ShellSkipMenu.propTypes = {
  /** Localization fuction */
  formatMessage: PropTypes.func,
  /** Array of items in the menu */
  items: PropTypes.array,
  /** Locale string for localization */
  locale: PropTypes.string,
  /** Function to update the visibility of the menu */
  onVisibilityChange: PropTypes.func,
  /** Whether or not the skip menu is open */
  open: PropTypes.bool
};

ShellSkipMenu.displayName = 'ShellSkipMenu';

const I18nShellSkipMenu = props => (
  <IntlProvider locale={props.locale} messages={localeDataSkipMenu}>
    <ShellSkipMenu {...props} />
  </IntlProvider>
);

export default I18nShellSkipMenu;
